import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, message, Modal, Spin, Alert, Tooltip } from 'antd';
import {
  UserOutlined, UpOutlined, DownOutlined,
  BarChartOutlined, SettingOutlined, UnorderedListOutlined, FilePdfOutlined,
  FullscreenOutlined, FullscreenExitOutlined, LeftOutlined, RightOutlined, FileTextOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { debounce } from 'lodash';
import { unstable_batchedUpdates } from 'react-dom';

import { getUserDetails, hasStudyViewingPermission, isValidDate } from '../../../../utils/helper';
import { useReportStatus } from '../../../../hooks/useReportStatus';
import useOoPluginBridge from '../../../../hooks/useOoPluginBridge';
import { launchViewerOnDualMonitor } from '../../../../utils/MonitorManager';
import { getReportPreviewSetting } from '../../../../utils/reportPreviewPref';
import { getReportingPanelPrefs, setStudiesRailCollapsed } from '../../../../utils/reportingPanelPref';
import { getEditorConfig, applyTemplateToDocument, applyPriorReportToDocument, forceSaveDocument, previewSignature, previewReportPdf, createScratchCopy, discardScratchCopy } from '../../../OnlyOfficeDocuments/documentsApi';
import useOnlyOfficeScript from '../../../OnlyOfficeDocuments/useOnlyOfficeScript';
import TemplatePanel from '../../../OnlyOfficeDocuments/TemplatePanel';
import PdfViewer from '../../../../components/PdfViewer';

import { useReportDataOodocs } from '../../hooks/useReportDataOodocs';
import { useReportActionsOodocs } from '../../hooks/useReportActionsOodocs';
import { useReportSession } from '../../hooks/useReportSession';
import { useInSessionCapture } from '../../hooks/useInSessionCapture';
import { useReportingLevel } from '../../hooks/useReportingLevel';
import { REPORT_CONFIG } from '../../constants';
import type { OrderDetails, OoReport, Report } from '../../types';

import StatusActions from './StatusActions';
import StudyQueue from '../StudyQueue/StudyQueue';
import ReportHistory from '../Panels/ReportHistory';
import PriorReports from '../Panels/PriorReports';
import AdvancedOptions from '../Panels/AdvancedOptions';
import CriticalFindingDialog from '../Dialogs/CriticalFindingDialog';
import TemplateLoadDialog from '../Dialogs/TemplateLoadDialog';
import InlineNotesPanel from './InlineNotesPanel';

import '../../styles/editor.css';

// Module-scope leave latch: unlike a ref, this survives component REMOUNTS within the
// same page instance (e.g. dev hot-reload, parent re-keying), so a remount during the
// close countdown cannot resurrect an editor. Keyed by order id so advancing to the
// NEXT queued study re-arms normally.
let pageLeavingOrderId: number | null = null;


// Trailing debounce on the storage flush. OnlyOffice emits a sync event per editing
// burst; this collapses a run of them into one forcesave without letting content sit
// only in the document server for long.
const FLUSH_DEBOUNCE_MS = 2500;

interface OOReportEditorProps {
  patientDetails: OrderDetails | null;
  /** Studies queue rendered at the top of the left rail */
  orders: OrderDetails[];
  selectedIndex: number;
  queueLoading?: boolean;
  onSelectOrder: (order: OrderDetails, index: number) => void;
  /** Remove a study from the loaded queue (unload). */
  onUnloadOrder: (order: OrderDetails, index: number) => void;
  handlePostSaving?: (status: string, patDetails: OrderDetails) => void;
  cancel: () => void;
  focusMode?: boolean;
  onToggleFocusMode?: () => void;
}

const OOReportEditor: React.FC<OOReportEditorProps> = ({
  patientDetails, orders, selectedIndex, queueLoading = false, onSelectOrder, onUnloadOrder,
  handlePostSaving, cancel, focusMode = false, onToggleFocusMode,
}) => {
  const editorInstanceRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // true when the doc was auto-created in this session (no prior content)
  const isNewDocRef = useRef(false);
  // True from the moment a commit hands off to close/advance until the next study loads.
  // Blocks any editor (re)creation so no OnlyOffice beforeunload guard exists at unload.
  const leavingRef = useRef(false);
  // Signature-preview lock plumbing. The lock stays ON from the moment a preview request
  // fires until the reopened editor's onDocumentReady confirms the new key is live (or the
  // request errors / times out). awaitingSigReadyRef tells onDocumentReady that the ready it
  // just saw is the post-preview reopen (so it should release the lock); sigTimeoutRef is the
  // 30s backstop that unlocks if that ready never arrives; sigLockOverlayRef is the transparent
  // blocker div we focus to pull keyboard focus out of the cross-origin OO iframe.
  const awaitingSigReadyRef = useRef(false);
  const sigTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sigLockOverlayRef = useRef<HTMLDivElement>(null);
  // Docs whose first autosave already triggered a history refresh. Only the REFETCH is
  // once-per-doc (it exists to make the private AUTOSAVED row appear in the list); the
  // forcesave itself repeats for the life of the session — see flushDocument.
  const autoStubFlushedRef = useRef<Set<number>>(new Set());
  // Documents the user has actually typed into this session. Only used to decide whether
  // switching the re-report source needs a confirmation — the scratch it replaces is deleted
  // server-side, so unsaved amendment text would go with it silently.
  const dirtyDocsRef = useRef<Set<number>>(new Set());
  // Trailing-debounce handle for the storage flush, so a burst of editor sync events
  // collapses into one forcesave.
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Orders for which a private working copy is currently being seeded — prevents a
  // double-copy if the open effect re-runs before the seeding refetch lands.
  const workingCopyInFlightRef = useRef<Set<number>>(new Set());
  // The throwaway scratch document forked from a signed report for re-reporting, keyed by
  // order AND by the signed document it was forked FROM. Held in a ref (not state) because
  // the unload handler must read the latest value without re-subscribing, and because it is
  // deliberately NOT part of render state — the UI still presents the signed report as the
  // current one.
  //
  // sourceDocId is part of the key because an order can hold MORE THAN ONE signed report
  // (re-report → sign off again). Keying on the order alone made the first fork answer every
  // later request, so selecting an older sign-off from Report History reopened the scratch
  // forked from the NEWEST one — the previous version appeared to contain the latest content.
  const scratchDocRef = useRef<{ orderId: number | null; sourceDocId: number | null; docId: number | null }>(
    { orderId: null, sourceDocId: null, docId: null }
  );
  // The in-flight fork request, shared by concurrent callers so a signed report is never
  // forked twice (and no caller falls back to opening the immutable snapshot). Keyed by the
  // source document for the same reason as above.
  const scratchForkInFlightRef = useRef<{ sourceDocId: number; promise: Promise<number | null> } | null>(null);
  // Monotonic id for editor-config loads, so an older in-flight load can't clobber a newer one.
  const configReqSeqRef = useRef(0);
  // Latest values for the stable OO event handlers (registered once per editor init)
  const editorStateRef = useRef<{
    currentReport: OoReport | null;
    activeDocId: number | null;
    refetch: (opts?: any) => void;
    // Is the CURRENT surface one a forcesave can meaningfully persist? False for a
    // read-only surface (nothing to save) and for a finalized snapshot (its file is
    // immutable — in-session edits go to a sidecar that only a sign-off consumes).
    flushEligible: boolean;
  }>({
    currentReport: null,
    activeDocId: null,
    refetch: () => {},
    flushEligible: false,
  });

  const [activeDocId, setActiveDocId] = useState<number | null>(null);
  // Force the OO editor into read-only for the active doc (historical versions of a signed order)
  const [docViewOnly, setDocViewOnly] = useState(false);

  // Switch the editing surface — the document AND whether it is read-only — as ONE update.
  //
  // The app mounts through the legacy ReactDOM.render root, so React does not batch state
  // updates made outside its own event handlers (a promise callback, a timer). Setting the
  // two separately therefore produces an intermediate render pairing the NEW read-only flag
  // with the OLD document id, and the load effect fires on that pair. After a sign-off the
  // old id is the scratch the server has just consumed, so that phantom load 404s and — being
  // a second in-flight request — can blank the config that did load. Same hazard flipping the
  // other way: an intermediate render would briefly open a committed document editable.
  const setSurface = useCallback((docId: number | null, viewOnly: boolean) => {
    unstable_batchedUpdates(() => {
      setDocViewOnly(viewOnly);
      setActiveDocId(docId);
    });
  }, []);

  // Status code of the commit currently in flight (spinner on that button, others locked)
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  // True from the moment a committed status starts its preview hold until the queue hands
  // off to the next study. Together with pendingAction it covers the whole commit window
  // (button click → save → preview → advance), during which the study queue must not accept
  // a selection or an unload — see StudyQueue's `disabled` prop for what the race does.
  const [handoffPending, setHandoffPending] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [onlyOfficeUrl, setOnlyOfficeUrl] = useState<string | null>(null);
  const [editorConfig, setEditorConfig] = useState<any>(null);
  // Proxy DRAFT is attribution-only: the draft is credited to the chosen radiologist
  // (report history + Reported By), but nothing is stamped into the document — a draft
  // is unsigned by definition. Proxy SIGNOFF is the one that stamps a signature.
  const [proxyDraftUser, setProxyDraftUser] = useState<string | null>(null);
  const [proxySignoffUser, setProxySignoffUser] = useState<string | null>(null);
  const [cosigningDoctor, setCosigningDoctor] = useState<string | null>(null);
  const [diagnosed, setDiagnosed] = useState<string | null>(null);
  const [correlated, setCorrelated] = useState<string | null>(null);
  const [criticalFindingVisible, setCriticalFindingVisible] = useState(false);
  // Read-only "what PDF am I about to produce" preview (see handlePreviewPdf).
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState<ArrayBuffer | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  // Studies rail state is a remembered per-user preference (user_preferences row, hydrated
  // into localStorage at boot) — a radiologist who closes the queue expects it to stay
  // closed in the next reporting tab too. The details pane below stays session-local.
  const [studiesCollapsed, setStudiesCollapsed] = useState(() => getReportingPanelPrefs().studiesCollapsed);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);

  // Fire-and-forget persist: the rail has already moved locally, so a failed save must not
  // interrupt reporting — it only means the choice won't follow the user to another machine.
  const toggleStudiesRail = useCallback((collapsed: boolean) => {
    setStudiesCollapsed(collapsed);
    setStudiesRailCollapsed(collapsed);
  }, []);
  // Right-panel tabs: the report editor plus inline HIS / RIS notes. The editor pane stays
  // mounted at all times (its OnlyOffice lifecycle must never be torn down by a tab switch);
  // the notes pane is lazy-mounted on first open, then kept alive.
  const [activeEditorTab, setActiveEditorTab] = useState<'editor' | 'his' | 'ris'>('editor');
  const [notesVisited, setNotesVisited] = useState(false);
  const [notesSection, setNotesSection] = useState<'his' | 'ris'>('ris');
  const openNotesTab = useCallback((sec: 'his' | 'ris') => {
    setNotesSection(sec);
    setActiveEditorTab(sec);
    setNotesVisited(true);
  }, []);
  // True while a signature-preview rewrite + editor reopen is in flight
  const [signatureUpdating, setSignatureUpdating] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    templateLoad: true,
    reportsHistory: true,
    priorReports: false,
    advancedOptions: true,
  });

  const pacs_order = patientDetails?.pacs_order;
  const patient = pacs_order?.patient;

  // ── Populate diagnosed/correlated from study-report-details ──
  useEffect(() => {
    const d = patientDetails?.ow_diagnosed;
    const c = patientDetails?.ow_correlated;
    setDiagnosed(d === true ? 'diagnosed' : d === false ? 'notdiagnosed' : null);
    setCorrelated(c === true ? 'correlated' : c === false ? 'notcorrelated' : null);
  }, [patientDetails?.ow_diagnosed, patientDetails?.ow_correlated]);

  // ── Hooks ──
  const { statusList, loading: statusLoading } = useReportStatus();
  const {
    reports, currentReport, setCurrentReport,
    priorReports, radUsers, allUsers,
    isSignedOff, initialFetchDone, refetchReports,
  } = useReportDataOodocs(patientDetails);

  // Is THIS order in the middle of a close/advance handoff? Combines the ref (fast,
  // same-mount) with the module latch (survives remounts).
  const isLeaving = useCallback(() => {
    return leavingRef.current ||
      (pageLeavingOrderId !== null && pageLeavingOrderId === pacs_order?.pacs_ord_id);
  }, [pacs_order?.pacs_ord_id]);

  const markLeaving = useCallback(() => {
    leavingRef.current = true;
    pageLeavingOrderId = pacs_order?.pacs_ord_id ?? null;
  }, [pacs_order?.pacs_ord_id]);

  // ── Persist the live session to storage ────────────────────────────────────────────
  // OnlyOffice's `customization.autosave` only governs the editor's own in-session
  // behaviour — nothing reaches OUR storage until the session closes (callback status 2)
  // or we ask for a forcesave (status 6). A close-only save lands seconds late and can be
  // dropped by the callback's staleness guard when a server-side rewrite has since bumped
  // doc_version, so every sync point is turned into a real forcesave here.
  //
  // Safe to repeat: the callback rotates doc_key only on status 2, never on a forcesave
  // (status 6), so a session can be flushed any number of times without breaking its key.
  //
  // Reads everything from refs and has no deps, so the OO event handlers — registered once
  // per editor init — can close over it exactly once and still act on current state.
  const doFlush = useCallback(() => {
    const { activeDocId: docId, refetch, flushEligible } = editorStateRef.current;
    // leavingRef rather than isLeaving(): a ref is always current, and this callback is
    // deliberately stable.
    if (!docId || !flushEligible || leavingRef.current) return;
    forceSaveDocument(docId)
      .then(() => {
        // The refetch is what surfaces the private AUTOSAVED entry in the history list.
        // Needed once per doc; later flushes only rewrite the file.
        if (autoStubFlushedRef.current.has(docId)) return;
        autoStubFlushedRef.current.add(docId);
        refetch();
      })
      .catch(() => { /* transient — the next sync point retries */ });
  }, []);

  const flushDocument = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      doFlush();
    }, FLUSH_DEBOUNCE_MS);
  }, [doFlush]);

  // Fire a queued flush immediately instead of losing it. Used when the surface is about
  // to be torn down while the OO session is still connected.
  const runPendingFlush = useCallback(() => {
    if (!flushTimerRef.current) return;
    clearTimeout(flushTimerRef.current);
    flushTimerRef.current = null;
    doFlush();
  }, [doFlush]);

  useEffect(() => () => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
  }, []);

  // ── Editor cleanup helper ──
  const destroyEditor = useCallback(() => {
    // Run any debounced flush NOW rather than dropping it — the OO session is still live
    // at this point (the iframe is detached below), so a queued forcesave would otherwise
    // be lost along with the keystrokes that scheduled it.
    runPendingFlush();
    // Detach-first: api.js's destroyEditor() tears the frame down by NAVIGATING it,
    // which fires the iframe's unsaved-changes beforeunload and makes Chrome raise a
    // blocking "Leave site?" dialog mid-call (always dirty on the re-signoff surface,
    // where autosave is off by design). Detaching the frame from the DOM instead
    // discards its document WITHOUT firing beforeunload — no dialog can appear. The
    // detach must RESTORE the container div in the frame's place: DocEditor replaced
    // it on mount, and a later DocEditor silently no-ops (spinner forever) when
    // getElementById can't find its placeholder id. The document server still saves
    // on session end (websocket disconnect → assembly → callback).
    try {
      document.querySelectorAll('iframe[name="frameEditor"]').forEach(f => {
        const placeholder = containerRef.current;
        if (placeholder && !placeholder.isConnected && f.parentNode) {
          f.parentNode.replaceChild(placeholder, f);
        } else {
          f.remove();
        }
      });
    } catch {}
    if (editorInstanceRef.current) {
      // Frame is already gone — this only cleans api.js's internal registry.
      try { editorInstanceRef.current.destroyEditor(); } catch {}
      editorInstanceRef.current = null;
    }
  }, [runPendingFlush]);

  // Hold the just-committed report on screen before the queue closes the window or
  // advances, so the user sees the final version. Reloading the doc read-only picks up the
  // server-side rewrite (e.g. the sign-off header/signature the backend adds). The hold
  // only happens when the preview is switched on for this user (see
  // utils/reportPreviewPref); otherwise the handoff is immediate.
  // After the hold, tear the OO editor down (detach — never fires the beforeunload "Leave
  // site?" dialog) and do NOT refetch: a refetch would reselect the report and resurrect a
  // fresh editable editor whose beforeunload guard would block the close. Content is safe —
  // ending the session assembles + saves server-side; forks flush/capture before copying.
  const holdPreviewThenHandoff = useCallback((status: string, previewDocId: number | null) => {
    if (!handlePostSaving) {
      refetchReports();
      return;
    }
    // Off unless this user (or the centre default behind them) asked for it — the hold
    // is dead time on a long queue. Read at commit time, never memoised, so changing the
    // setting in the PACS tab takes effect on the very next report in this window.
    const { enabled: previewEnabled, seconds: previewSeconds } = getReportPreviewSetting();

    if (previewEnabled && previewDocId != null) {
      setSurface(previewDocId, true);
    }

    setHandoffPending(true);

    // With no preview there is nothing left to show, so tear the live editor down NOW
    // instead of in the timer. Order matters: destroyEditor() first, because it runs any
    // debounced flush and markLeaving() would suppress that (doFlush bails while leaving);
    // in the preview path the same flush happens when the read-only reload replaces the
    // editor. Then latch "leaving" — setPendingAction(null) lands, and re-runs the surface
    // effect, before the timer fires, and without the latch that effect would re-derive an
    // editing surface (forking a fresh scratch for the study just signed) only for the
    // handoff a tick later to throw it away.
    if (!previewEnabled) {
      destroyEditor();
      markLeaving();
    }

    // One duration for both outcomes (close the window / advance to the next study);
    // 0 collapses the hold to the next tick, which is still needed so the state updates
    // above are committed before the editor is torn down.
    const holdMs = previewEnabled ? previewSeconds * 1000 : 0;

    setTimeout(() => {
      markLeaving();
      destroyEditor();
      handlePostSaving(status, patientDetails);
    }, holdMs);
  }, [refetchReports, handlePostSaving, patientDetails, destroyEditor, markLeaving, setSurface, selectedIndex]);

  const onSaved = useCallback((status: string, previewDocId: number) => {
    message.success(status === 'SIGNEDOFF' ? 'Report signed off' : 'Updated successfully');
    // A sign-off consumes the scratch surface server-side (its content is now the signed
    // document). Forget it here so the leave-cleanup doesn't chase a deleted row and the
    // next study starts without inheriting it.
    scratchDocRef.current = { orderId: null, sourceDocId: null, docId: null };
    // Every committed status (Draft / Reviewed / Sign off / re-sign-off) completes work on
    // this study: show the committed report read-only, then close (single-study queue) or
    // advance to the next item. On sign-off the backend mints a NEW signed doc, so preview
    // the doc the backend returned (previewDocId) — reopening the original would show the
    // stale, header-less draft.
    holdPreviewThenHandoff(status, previewDocId ?? currentReport?.document?.doc_id ?? activeDocId);
  }, [holdPreviewThenHandoff, currentReport, activeDocId]);

  const { saveReport, deleteReport, createWorkingCopy, createNewDocument, saveAsDraft, saveAsReviewed } = useReportActionsOodocs({
    orderDetails: patientDetails,
    currentReport,
    activeDocId,
    docViewOnly,
    isSignedOff,
    onSaved,
  });

  // ── Re-reporting surface for a signed study ──
  // The signed .docx is an immutable snapshot, so it is never opened for editing directly.
  // Fork it into a throwaway scratch document instead: a real file the editor can autosave
  // to and the signature preview can stamp, with no report row behind it — so Report History
  // is unchanged and abandoning the amendment loses it, exactly as before.
  //
  // Reused for the rest of the session once created; a fresh open forks again from the
  // pristine snapshot (the backend drops the caller's previous scratch first).
  const forkScratchSurface = useCallback((signedDocId: number): Promise<number | null> => {
    const orderId = pacs_order?.pacs_ord_id ?? null;
    if (orderId == null) return Promise.resolve(null);

    const held = scratchDocRef.current;
    // Reuse only a scratch forked from THIS signed document — a scratch made from a different
    // sign-off of the same order holds that version's content, not this one's.
    if (held.docId && held.orderId === orderId && held.sourceDocId === signedDocId) {
      return Promise.resolve(held.docId);
    }
    // Both open paths (initial load and selecting the signed report from history) can ask at
    // once. They must share ONE fork: a second request would fork again server-side, and a
    // caller that simply bailed would fall back to opening the snapshot itself. Only requests
    // for the SAME source share it.
    const inFlight = scratchForkInFlightRef.current;
    if (inFlight && inFlight.sourceDocId === signedDocId) return inFlight.promise;

    const run = createScratchCopy(signedDocId)
      .then(res => {
        const newDocId: number | null = res.data?.data?.doc_id ?? null;
        if (newDocId) scratchDocRef.current = { orderId, sourceDocId: signedDocId, docId: newDocId };
        return newDocId;
      })
      .catch((err: any) => {
        console.error('Failed to open the signed report for re-reporting:', err);
        message.error(err.response?.data?.message || 'Failed to open the report for re-reporting');
        return null;
      })
      .finally(() => {
        if (scratchForkInFlightRef.current?.sourceDocId === signedDocId) {
          scratchForkInFlightRef.current = null;
        }
      });

    scratchForkInFlightRef.current = { sourceDocId: signedDocId, promise: run };
    return run;
  }, [pacs_order?.pacs_ord_id]);

  // Throw away the scratch surface when the amendment is abandoned. Best-effort and silent:
  // the backend sweeps anything this misses, and the user is on their way out.
  const discardScratchSurface = useCallback(() => {
    const { docId } = scratchDocRef.current;
    scratchDocRef.current = { orderId: null, sourceDocId: null, docId: null };
    scratchForkInFlightRef.current = null;
    if (!docId) return;
    discardScratchCopy(docId).catch(() => {});
  }, []);

  // Effective order state derived from committed reports (private autosaves don't count).
  // Drives forward-only button gating: SCANNED → DRAFTED → REVIEWED → SIGNEDOFF.
  const orderReportState = useMemo(() => {
    const statuses = reports.map(r => r.pr_status).filter(Boolean);
    if (statuses.includes('SIGNEDOFF')) return 'SIGNEDOFF';
    if (statuses.includes('REVIEWED')) return 'REVIEWED';
    if (statuses.includes('DRAFTED')) return 'DRAFTED';
    return 'SCANNED';
  }, [reports]);

  // The newest signed report on this order, and the document behind it. An order can carry
  // several sign-offs (each re-report adds one), and only the newest is the live report: it
  // is what the study list and any download resolve to, and it is the one a re-report amends.
  // Older sign-offs are historical records — opened for reading, never forked for editing.
  const latestSignedReport = useMemo(() => {
    const signed = reports.filter(
      r => r.pr_status === 'SIGNEDOFF' && (r as OoReport).document?.doc_id
    ) as OoReport[];
    if (!signed.length) return null;
    return signed.reduce((newest, r) => ((r.pr_id ?? 0) > (newest.pr_id ?? 0) ? r : newest));
  }, [reports]);

  // This user's effective reporting level for the current study (center × modality).
  // Gates the Draft/Reviewed/Sign-off buttons when the backend is enforcing.
  const reportingLevel = useReportingLevel(pacs_order?.po_center_code, pacs_order?.po_modality);

  // Non-null == the lookup has settled (it resolves to a sentinel even on failure), so the
  // editor can wait for it instead of opening editable and flipping to read-only a moment
  // later — that would mean a second destroyEditor/init cycle on every open.
  const levelSettled = reportingLevel != null;

  // Below DRAFT (i.e. View or No Access) there is nothing this user can ever save, so the
  // document opens read-only. The server enforces this independently in getEditorConfig;
  // this only spares the user a surface that looks writable but silently discards work.
  const privilegeReadOnly =
    !!reportingLevel?.enforcement_enabled &&
    reportingLevel.level_ordinal != null &&
    reportingLevel.level_ordinal < 2; // 2 = DRAFT

  // Whose signature would this sign-off stamp? On a proxy SIGN-OFF it is the radiologist of
  // record (updateDocxHeaderOnSignoff stamps `proxy_user || user_id`); otherwise the
  // logged-in user. A proxy DRAFT is attribution only and stamps nothing, so it is not part
  // of this. With no signature for that identity the report finalizes unsigned — block it.
  // The proxy dropdown only offers users who have a signature, so a selected proxy signer
  // always satisfies this.
  const signatureMissing =
    !proxySignoffUser && reportingLevel != null && reportingLevel.has_signature === false;

  useReportSession(pacs_order?.pacs_ord_id);

  const { loaded: scriptLoaded, error: scriptError, retry: retryScript } = useOnlyOfficeScript(onlyOfficeUrl);

  // Keep the OO event handlers' view of the world current (they are registered once
  // per editor init and would otherwise close over stale state).
  useEffect(() => {
    editorStateRef.current = {
      currentReport,
      activeDocId,
      refetch: refetchReports,
      // Decided by the SURFACE, not by pr_status: the re-report scratch and the working
      // copy forked from a committed draft are both real drafts while currentReport still
      // points at the signed / committed report. Only the finalized doc opened directly
      // (activeDocId === that report's own document) is ineligible.
      flushEligible:
        !docViewOnly &&
        !privilegeReadOnly &&
        !(currentReport?.document?.doc_id === activeDocId
          && currentReport?.document?.doc_status === 'finalized'),
    };
  }, [currentReport, activeDocId, refetchReports, docViewOnly, privilegeReadOnly]);

  // Capture the live editor content for sign-off. See the hook for why the file on disk
  // cannot be trusted as the source of a signed report.
  const { requestCurrentDocUrl, handleDownloadAs } = useInSessionCapture(editorInstanceRef);

  // ── Set active doc from current report ──
  // The editing surface is always the user's OWN document: a blank private stub (fresh
  // study), a private working copy seeded from a committed draft/reviewed report, or a
  // finalized signed snapshot (re-report scratchpad). A committed draft/reviewed report is
  // NEVER edited in place — autosave would overwrite the document everyone shares.
  useEffect(() => {
    if (!initialFetchDone || isLeaving()) return; // wait for first fetch; never during close/advance

    // A commit is in flight, or has just landed and parked the editor on the committed
    // document for the read-only preview. Re-deriving the surface here would replace that
    // preview — and on a signed order it would fork a brand-new scratch for a report the
    // user has just finished signing. (The sign-off response re-selects the new SIGNEDOFF
    // report, which changes currentReport and re-runs this effect.) Also covers browsing a
    // historical version read-only, where the surface is likewise deliberate.
    if (pendingAction || docViewOnly) return;

    const rep = currentReport;

    // No reports exist yet — auto-create a blank document. The backend links it to a private
    // AUTOSAVED row; the order stays SCANNED until the user explicitly clicks Draft.
    // A read-only user must not mint documents (a blank stub here, a working copy below):
    // both write rows for someone who can never commit them. With nothing to open, the
    // editor stays empty and the banner explains why.
    if (privilegeReadOnly) {
      const existing = currentReport?.document?.doc_id;
      if (existing) setActiveDocId(existing);
      return;
    }

    if (rep === null && reports.length === 0) {
      createNewDocument().then(docId => {
        if (docId) {
          isNewDocRef.current = true;
          setSurface(docId, false);
          refetchReports();
        }
      });
      return;
    }

    const doc = rep?.document;
    if (!doc?.doc_id) return;

    const isFinalized = doc.doc_status === 'finalized';
    const isMineWorkingCopy = !rep!.pr_status || rep!.pr_status === 'AUTOSAVED';

    // Committed DRAFTED/REVIEWED report and this user has no working copy yet: seed a private
    // copy from its content and edit THAT. selectDocId steers the refetch onto the new copy so
    // it — not the committed source — becomes the surface (otherwise the "keep current
    // selection" rule would re-select the committed report and loop straight back here).
    if (!isFinalized && !isMineWorkingCopy) {
      const orderId = pacs_order?.pacs_ord_id;
      if (orderId == null || workingCopyInFlightRef.current.has(orderId)) return;
      workingCopyInFlightRef.current.add(orderId);
      createWorkingCopy(doc.doc_id)
        .then(newDocId => {
          if (newDocId) {
            setSurface(newDocId, false);
            refetchReports({ selectDocId: newDocId });
          }
        })
        .finally(() => { workingCopyInFlightRef.current.delete(orderId); });
      return;
    }

    // Signed snapshot: never edited in place. Fork it into a throwaway scratch document and
    // make THAT the surface, leaving currentReport pinned to the signed report as the action
    // context (the same split the template-load-on-a-signed-study path already uses).
    if (isFinalized) {
      // Already re-reporting on a scratch for this order: leave the surface where it is.
      // currentReport can move under the user (a refetch re-selecting the newest sign-off),
      // and re-deriving here would fork a SECOND scratch — discarding the amendment in
      // progress, possibly one started from a deliberately chosen older version.
      const held = scratchDocRef.current;
      if (held.docId && held.orderId === pacs_order?.pacs_ord_id && held.docId === activeDocId) return;
      forkScratchSurface(doc.doc_id).then(scratchId => {
        // Fork failed (already reported) — fall back to the snapshot so the doctor can still
        // read and re-report it, exactly as before this change.
        setSurface(scratchId ?? doc.doc_id, false);
      });
      return;
    }

    // Own working copy — resume editing it.
    setSurface(doc.doc_id, false);
  }, [currentReport?.pr_id, initialFetchDone, privilegeReadOnly, pendingAction, docViewOnly]);

  // ── Fetch OnlyOffice editor config when activeDocId changes ──
  const loadEditorConfig = useCallback(async (docId: number, viewOnly = false) => {
    // Only the most recent request may touch state. Two loads can be in flight at once
    // (a doc switch that also flips view-only), and without this the loser settling last
    // overwrites the winner — a stale 404 blanking a config that had already loaded.
    const seq = ++configReqSeqRef.current;
    const isStale = () => seq !== configReqSeqRef.current;

    setEditorLoading(true);
    setEditorError(null);
    setEditorConfig(null);

    try {
      const res = await getEditorConfig(docId, viewOnly ? 'view' : 'edit');
      if (isStale()) return;
      const { config, onlyOfficeUrl: url } = res.data.data;
      setEditorConfig(config);
      setOnlyOfficeUrl(url);
    } catch (err: any) {
      if (isStale()) return;
      // Log the id: an editor error names the symptom, not the document that caused it, and
      // the usual cause is a stale id surviving a commit (a consumed re-reporting scratch).
      console.error(`[report-editor] failed to open document ${docId} (viewOnly=${viewOnly})`, err?.response?.status, err?.response?.data);
      setEditorError(err.response?.data?.message || 'Failed to load editor configuration');
      setEditorLoading(false);
    }
  }, []);

  // A DIFFERENT study arriving (queue advance) re-arms the editor lifecycle. A remount
  // of the same order during the close countdown does NOT (module latch still set).
  useEffect(() => {
    leavingRef.current = false;
    setHandoffPending(false);
    if (pageLeavingOrderId !== null && pageLeavingOrderId !== pacs_order?.pacs_ord_id) {
      pageLeavingOrderId = null;
    }
  }, [patientDetails]);

  useEffect(() => {
    // levelSettled gates the FIRST load so the mode is right on the initial init.
    if (!activeDocId || isLeaving() || !levelSettled) return;
    destroyEditor();
    loadEditorConfig(activeDocId, docViewOnly || privilegeReadOnly);
  }, [activeDocId, docViewOnly, levelSettled, privilegeReadOnly]);

  // ── Drop the previous study's editing surface whenever the active order changes ──
  // Covers the queue advancing to the next study AND the parent setting the active order to
  // null when a claim fails (report limit / lock / conflict). The reload effects above are
  // keyed on activeDocId / currentReport, neither of which changes at the moment the order
  // does — so without this, activeDocId, editorConfig and docViewOnly all still describe the
  // study that was just popped. That strands its document — with its baked-in patient header
  // — on screen under the new patient's details (the cross-patient mismatch a doctor
  // reported), and leaves the commit buttons pointed at that document. Tearing the surface
  // down also restores the !activeDocId overlay ("Loading…" / "Select a study…") in place of
  // a silently blank pane.
  // Guarded by isLeaving so the close/advance preview-hold teardown isn't disturbed; on a
  // genuine advance the re-arm effect above runs first in the same commit, so the guard is
  // already released by the time this runs.
  useEffect(() => {
    if (isLeaving()) return;
    destroyEditor();
    setActiveDocId(null);
    setEditorConfig(null);
    setEditorError(null);
    setEditorLoading(false);
    setDocViewOnly(false);
    // A fresh study has no doc auto-created in this session yet, so Load Template must go
    // back to asking append-vs-replace instead of silently replacing.
    isNewDocRef.current = false;
  }, [pacs_order?.pacs_ord_id]);

  // Returning to the Editor tab un-hides the OO container (display:none → flex). Some OO
  // builds paint blank until the next layout tick, so nudge them with a resize event.
  useEffect(() => {
    if (activeEditorTab !== 'editor') return;
    const t = setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
    return () => clearTimeout(t);
  }, [activeEditorTab]);

  // ── Initialize OnlyOffice editor once script + config are ready ──
  useEffect(() => {
    if (!scriptLoaded || !editorConfig || !containerRef.current) return;
    // Never (re)create an editor while closing/advancing — a live OO instance at unload
    // time pops the browser's "Leave site?" prompt.
    if (isLeaving()) return;

    const timer = setTimeout(() => {
      if (isLeaving()) return;
      try {
        editorInstanceRef.current = new (window as any).DocsAPI.DocEditor(
          'oo-report-editor-container',
          {
            ...editorConfig,
            events: {
              ...editorConfig.events,
              onDocumentReady: () => {
                setEditorLoading(false);
                // The reopened editor for a signature preview is now fully loaded on the new
                // key — this is the ONLY point at which the lock is released on success.
                if (awaitingSigReadyRef.current) {
                  awaitingSigReadyRef.current = false;
                  if (sigTimeoutRef.current) { clearTimeout(sigTimeoutRef.current); sigTimeoutRef.current = null; }
                  setSignatureUpdating(false);
                }
              },
              onError: (event: any) => {
                console.error('OnlyOffice editor error:', event);
                message.error('Editor encountered an error');
                setEditorLoading(false);
              },
              // data === true → the user has just made the document dirty; data === false
              // → those changes reached the document server.
              onDocumentStateChange: (event: any) => {
                // ANY state change means the user has typed, so the doc is no longer the
                // blank stub it was created as. Clearing the flag here (on the dirty edge,
                // not on the later sync) is what stops Load Template from silently
                // replacing work the user has already entered.
                isNewDocRef.current = false;
                const dirtyId = editorStateRef.current.activeDocId;
                if (dirtyId && editorStateRef.current.flushEligible) dirtyDocsRef.current.add(dirtyId);
                if (event?.data === false) flushDocument();
              },
              // Answer to our downloadAs() call — URL of the assembled current content
              onDownloadAs: handleDownloadAs,
            },
          },
        );
      } catch (err) {
        console.error('Failed to initialize OnlyOffice editor:', err);
        setEditorError('Failed to initialize editor');
        setEditorLoading(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [scriptLoaded, editorConfig]);

  // Cleanup on unmount
  useEffect(() => () => destroyEditor(), []);

  // No idle detection here by design. An open reporting tab means the study is
  // being worked on, full stop — the reporting lock stays held until the tab
  // closes or the report is committed.
  //
  // This used to release the lock after 60s of no mouse/keyboard activity on the
  // PARENT document. OnlyOffice runs in an iframe, so typing and dictating inside
  // the editor never reached those listeners: a radiologist writing a report for
  // 90 seconds straight silently unlocked their own study, the "R" badge vanished
  // while they were still working, and another user could claim the study from
  // under them. Walking away is already covered server-side — the keep-alive ping
  // stops with the tab and cleanupInactiveStudies reclaims the row.

  // ── OnlyOffice Enhanced Paste plugin integration ──
  // Hands the plugin its auth token and makes its one whitelisted API call
  // on its behalf (rich paste from the old PACS).
  useOoPluginBridge(activeDocId);

  // ── Handlers ──
  const proxyMode = proxyDraftUser ? 'draft' : proxySignoffUser ? 'signoff' : null;

  const handleSaveAsDraft = useCallback(async () => {
    const newDocId = await saveAsDraft(proxyDraftUser ?? undefined);
    if (newDocId) {
      if (handlePostSaving) {
        // Drafting completes work on this study — preview the forked draft read-only, then
        // close / advance the queue.
        holdPreviewThenHandoff('DRAFTED', newDocId);
      } else {
        // Standalone fallback: switch the editor to the forked draft.
        setSurface(newDocId, false);
        refetchReports({ selectDocId: newDocId });
      }
    }
  }, [saveAsDraft, proxyDraftUser, refetchReports, handlePostSaving, holdPreviewThenHandoff, setSurface]);

  const handleSaveForm = useCallback((statusCode: string) => {
    if (pendingAction) return; // an action is already in flight
    if (signatureUpdating) return; // editor is locked while a signature preview rebuilds

    // For sign-off, both diagnosed and correlated must be selected — only when the
    // FORCE_DIAGNOSIS_SIGNOFF flag is on (defaults off).
    if (REPORT_CONFIG.FORCE_DIAGNOSIS_SIGNOFF && statusCode === 'SIGNEDOFF' && (!diagnosed || !correlated)) {
      // Make sure the user can see the controls they need to fill in
      setExpandedSections(prev => ({ ...prev, advancedOptions: true }));
      message.error('Please select both "Clinically Diagnosed" and "Clinically Correlated" before signing off');
      return;
    }

    // Reviewed: the working surface is the user's private copy — promote it in place to a
    // REVIEWED entry (order → REVIEWED), then complete work on this study (close / advance).
    // Draft is locked once the order is REVIEWED; the workflow never regresses. The fork path
    // is kept as a fallback for a committed surface (e.g. the standalone embedding).
    if (statusCode === 'REVIEWED') {
      const isAutoStub = !currentReport?.pr_status || currentReport.pr_status === 'AUTOSAVED';
      setPendingAction('REVIEWED');
      if (isAutoStub) {
        saveReport('REVIEWED', { proxy_user: proxyDraftUser })
          .finally(() => setPendingAction(null));
        return;
      }
      saveAsReviewed(proxyDraftUser ?? undefined).then(newDocId => {
        if (newDocId) {
          if (handlePostSaving) {
            holdPreviewThenHandoff('REVIEWED', newDocId);
          } else {
            setSurface(newDocId, false);
            refetchReports({ selectDocId: newDocId });
          }
        }
      }).finally(() => setPendingAction(null));
      return;
    }

    // Draft: the working surface is the user's private copy — promote it in place to a
    // DRAFTED entry (order → DRAFTED, the entry becomes a shared draft); the study is then
    // complete for this sitting and the queue closes / advances (via onSaved). The fork path
    // is kept as a fallback for a committed surface (e.g. the standalone embedding).
    if (statusCode === 'DRAFTED') {
      const isAutoStub = !currentReport?.pr_status || currentReport.pr_status === 'AUTOSAVED';
      setPendingAction('DRAFTED');
      if (!isAutoStub) {
        handleSaveAsDraft().finally(() => setPendingAction(null));
        return;
      }
      saveReport('DRAFTED', { proxy_user: proxyDraftUser, co_signing_doctor: cosigningDoctor })
        .finally(() => setPendingAction(null));
      return;
    }

    const proxyUser = statusCode === 'SIGNEDOFF' ? proxySignoffUser : proxyDraftUser;

    // Convert radio button values to boolean
    const payload: any = {
      proxy_user: proxyUser,
      co_signing_doctor: cosigningDoctor,
    };

    if (diagnosed) {
      payload.diagnosed = diagnosed === 'diagnosed';
    }
    if (correlated) {
      payload.correlated = correlated === 'correlated';
    }

    // EVERY sign-off extracts the live in-session content via downloadAs and sends its URL
    // along — the backend uses it as the source of the new signed report instead of reading
    // the file on disk.
    //
    // This used to be limited to finalized re-signs, on the assumption that a working draft
    // "autosaves reliably". It does not: there is no periodic writeback, and a forcesave
    // issued within ~2-3s of the last keystroke assembles a file without it — sometimes
    // reporting success while doing so. That is how a signed report ends up containing the
    // bare template while the draft (saved seconds later, on editor close) holds everything.
    // downloadAs is the only capture that includes content the document server hasn't
    // received yet, so the fragile path must not be the default one.
    //
    // A null URL means capture FAILED (editor not mounted / API unavailable / timeout) — not
    // that there was nothing to capture. We still submit, and the backend falls back to a
    // verified forcesave and refuses the sign-off if even that can't be confirmed.
    // Skip only for read-only historical views (docViewOnly).
    setPendingAction(statusCode);
    if (statusCode === 'SIGNEDOFF' && !docViewOnly && activeDocId) {
      requestCurrentDocUrl()
        .then(amendUrl => saveReport(statusCode, amendUrl ? { ...payload, amend_file_url: amendUrl } : payload))
        .finally(() => setPendingAction(null));
      return;
    }

    saveReport(statusCode, payload).finally(() => setPendingAction(null));
  }, [saveReport, saveAsReviewed, handleSaveAsDraft, holdPreviewThenHandoff, requestCurrentDocUrl, refetchReports, handlePostSaving, pendingAction, signatureUpdating, currentReport, activeDocId, docViewOnly, proxyDraftUser, proxySignoffUser, cosigningDoctor, diagnosed, correlated]);

  const handleLoadReport = useCallback(async (report: Report) => {
    const ooReport = report as OoReport;
    const docId = ooReport.document?.doc_id;
    if (!docId) return;

    // Below DRAFT level there is nothing this user can ever commit, so every document opens
    // read-only and none is ever forked — minting an editing surface for someone who cannot
    // save it just leaves scratch rows behind. (The initial-open effect gates this too; this
    // is the click path.)
    if (privilegeReadOnly) {
      setCurrentReport(ooReport);
      setSurface(docId, true);
      return;
    }

    if (ooReport.document?.doc_status === 'finalized') {
      // Re-reporting: edit a throwaway scratch fork of THIS signed version, never the
      // immutable snapshot itself. ANY sign-off in the history can be the starting point —
      // picking an older one is how a doctor re-reports from a version a later sign-off
      // superseded. The version forked from becomes the action context, so Report History
      // marks the one actually on screen. A sign-off is unaffected either way: it always
      // writes a BRAND-NEW signed report (createSignedReport never mutates its source), so
      // the whole chain of sign-offs survives whichever link the amendment started from.
      //
      // Exactly one scratch exists per user per order — the backend drops the previous one
      // when it forks the next. Confirm first when that would throw away typing: the scratch
      // has no report row, so nothing recovers it once it is gone.
      const held = scratchDocRef.current;
      const discardsEdits =
        !!held.docId && held.sourceDocId !== docId && dirtyDocsRef.current.has(held.docId);
      if (discardsEdits) {
        const proceed = await new Promise<boolean>(resolve => {
          Modal.confirm({
            title: 'Discard your current re-report edits?',
            content:
              'Opening a different signed version replaces the working copy you have open. ' +
              'Changes made since you opened it are not saved anywhere and cannot be recovered.',
            okText: 'Discard and open',
            okButtonProps: { danger: true },
            cancelText: 'Keep editing',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!proceed) return;
        dirtyDocsRef.current.delete(held.docId!);
      }
      setCurrentReport(ooReport);
      const scratchId = await forkScratchSurface(docId);
      setSurface(scratchId ?? docId, false);
      return;
    }
    if (orderReportState === 'SIGNEDOFF') {
      // Once the order is signed, historical drafts/reviews are read-only records.
      // Keep the signed report as the action context — only the viewer doc changes.
      setSurface(docId, true);
      return;
    }

    const isMineWorkingCopy = !ooReport.pr_status || ooReport.pr_status === 'AUTOSAVED';
    if (isMineWorkingCopy) {
      // The user's own private working copy — resume editing it directly.
      setCurrentReport(ooReport);
      setSurface(docId, false);
      return;
    }

    // A committed draft/reviewed entry: branch a private working copy from THIS version
    // instead of editing the shared document in place (autosave would overwrite it).
    const orderId = pacs_order?.pacs_ord_id;
    if (orderId == null || workingCopyInFlightRef.current.has(orderId)) return;
    workingCopyInFlightRef.current.add(orderId);
    try {
      const newDocId = await createWorkingCopy(docId);
      if (newDocId) {
        setSurface(newDocId, false);
        refetchReports({ selectDocId: newDocId });
      }
    } finally {
      workingCopyInFlightRef.current.delete(orderId);
    }
  }, [setCurrentReport, orderReportState, privilegeReadOnly, createWorkingCopy, forkScratchSurface, refetchReports, setSurface, pacs_order?.pacs_ord_id]);

  // ── Apply template / prior report to current document ──
  // Both go through the same backend merge (body only, this study's header/footer kept) and
  // the same append/replace prompt, so they share the dialog and the post-apply reload.
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);
  const [pendingPriorDocId, setPendingPriorDocId] = useState<number | null>(null);
  const [templateLoadDialogVisible, setTemplateLoadDialogVisible] = useState(false);

  // The queue can advance on the preview-hold timer while the append/replace dialog is open,
  // which would leave the PREVIOUS study's template pending against the new patient's
  // document. Drop it whenever the active order changes.
  useEffect(() => {
    setTemplateLoadDialogVisible(false);
    setPendingTemplateId(null);
    setPendingPriorDocId(null);
  }, [pacs_order?.pacs_ord_id]);

  const handleInsertTemplate = useCallback((templateId: number) => {
    if (!activeDocId) {
      message.error('No active document');
      return;
    }
    setPendingTemplateId(templateId);
    // Clear any prior-report selection so the shared dialog routes to the template path.
    setPendingPriorDocId(null);
    // Doc was just created in this session (no prior content) — auto-replace without asking
    if (isNewDocRef.current) {
      applyTemplate(templateId, 'replace');
    } else {
      setTemplateLoadDialogVisible(true);
    }
  }, [activeDocId]);

  // Shared post-merge handling for template + prior-report loads (identical response shape).
  const runDocMerge = useCallback(async (
    request: () => Promise<any>,
    action: 'append' | 'replace',
    label: string,
  ) => {
    if (!activeDocId) return;
    setTemplateLoadDialogVisible(false);
    try {
      const res = await request();
      // Reopen on the doc_id the backend returns. For a plain draft it equals activeDocId
      // (reload in place). For a signed-off study the finalized snapshot is immutable, so the
      // backend merges into a fresh editable working copy and returns ITS id.
      const newDocId: number = res.data?.data?.doc_id ?? activeDocId;
      isNewDocRef.current = false;
      // A server-side merge produces NO editor event — the file is rewritten behind the
      // session and the editor reopens on the rotated key, so from OnlyOffice's point of
      // view nothing was edited and onDocumentStateChange never fires. Re-arm the
      // once-per-doc history refresh for both ids so the first real keystroke after the
      // merge surfaces the autosave entry (without this, a doc that had already been
      // flushed once before the merge could never refresh the list again).
      autoStubFlushedRef.current.delete(activeDocId);
      autoStubFlushedRef.current.delete(newDocId);
      if (newDocId !== activeDocId) {
        // Switch the editing SURFACE to the new working copy but keep currentReport on the
        // signed report — sign-off then captures this live session (downloadAs → amend_file_url),
        // the same proven path a text re-edit uses. Reassigning activeDocId re-runs the load
        // effect, which tears down and reopens the editor on the new doc.
        // Deliberately NOT refetching here: re-selecting would move currentReport off the
        // signed report and break that capture path.
        setSurface(newDocId, false);
      } else {
        destroyEditor();
        loadEditorConfig(newDocId);
        // The backend bumped doc_version and rewrote the file, but the reports list still
        // describes the pre-merge state — which is why a loaded template appeared to never
        // autosave. selectDocId keeps the selection on the doc we just merged into.
        refetchReports({ selectDocId: newDocId });
      }
      message.success(`${label} ${action === 'append' ? 'appended' : 'applied'}`);
    } catch (err: any) {
      console.error(`Failed to apply ${label.toLowerCase()}:`, err);
      message.error(err.response?.data?.message || `Failed to apply ${label.toLowerCase()}`);
    } finally {
      setPendingTemplateId(null);
      setPendingPriorDocId(null);
    }
  }, [activeDocId, destroyEditor, loadEditorConfig, setSurface, refetchReports]);

  const applyTemplate = useCallback((templateId: number, action: 'append' | 'replace') => {
    if (!activeDocId || !templateId) return;
    return runDocMerge(
      () => applyTemplateToDocument(activeDocId, templateId, action),
      action,
      'Template',
    );
  }, [activeDocId, runDocMerge]);

  const applyPriorReport = useCallback((sourceDocId: number, action: 'append' | 'replace') => {
    if (!activeDocId || !sourceDocId) return;
    return runDocMerge(
      () => applyPriorReportToDocument(activeDocId, sourceDocId, action),
      action,
      'Prior report',
    );
  }, [activeDocId, runDocMerge]);

  // Loading a prior report behaves exactly like loading a template: prompt for
  // append/replace, then merge only its body into the CURRENT document. The prior's
  // header banner, footer and signature block are dropped by the backend.
  const handleLoadPriorReport = useCallback((report: Report) => {
    const sourceDocId = (report as OoReport).document?.doc_id;
    if (!sourceDocId) {
      message.error('Prior report has no linked document');
      return;
    }
    if (!activeDocId) {
      message.error('No active document');
      return;
    }
    setPendingPriorDocId(sourceDocId);
    // Doc was just created in this session (no prior content) — auto-replace without asking
    if (isNewDocRef.current) {
      applyPriorReport(sourceDocId, 'replace');
    } else {
      setTemplateLoadDialogVisible(true);
    }
  }, [activeDocId, applyPriorReport]);

  // ── "What will the PDF look like?" ──
  // Renders the report through the SIGN-OFF pipeline on the backend (in-session capture →
  // snapshot → header/signature/QR stamp → the same ConvertService call every later
  // download uses) against a throwaway copy, so what the doctor sees here is the document
  // they will get, not an approximation of it. Converting the working document instead
  // would show none of the sign-off stamping: no signing doctor in the letterhead, no
  // signoff time, no signature, no QR.
  //
  // Nothing is committed and the editing session is left alone — no report row, no status
  // change, and (unlike the live signature preview) no rewrite of the working file and no
  // doc_key rotation, so the doctor can carry straight on typing behind the modal.
  const handlePreviewPdf = useCallback(async () => {
    if (!activeDocId) return;

    setPdfPreviewOpen(true);
    setPdfPreviewData(null);
    setPdfPreviewLoading(true);
    try {
      // The same capture sign-off performs. A null URL is tolerated here where sign-off
      // aborts: the backend falls back to a gated forcesave, and a preview is not
      // irreversible. What it must never do is silently render stale content — that case
      // comes back as a 503 below.
      const amendUrl = await requestCurrentDocUrl();
      const blob = await previewReportPdf(activeDocId, {
        // Signature-bearing proxy only, exactly as the sign-off payload does — a proxy
        // draft re-attributes the report but stamps no signature.
        proxy_user: proxySignoffUser,
        co_signing_doctor: cosigningDoctor,
        amend_file_url: amendUrl,
      });
      setPdfPreviewData(await blob.arrayBuffer());
    } catch (err: any) {
      // The response body is a Blob (responseType), so the status is the only thing worth
      // branching on without parsing it back.
      const status = err?.response?.status;
      setPdfPreviewOpen(false);
      message.error(
        status === 503
          ? 'Could not capture your latest edits. Please try the preview again in a moment.'
          : 'Failed to generate the report preview'
      );
    } finally {
      setPdfPreviewLoading(false);
    }
  }, [activeDocId, proxySignoffUser, cosigningDoctor, requestCurrentDocUrl]);

  // ── Live signature preview ──
  // When the proxy-signoff / co-signing dropdowns change, ask the backend to stamp the chosen
  // signatures into the doc, then reopen the editor on the rewritten key — the same
  // teardown/reload the apply-template flow uses (loadEditorConfig re-fetches the fresh
  // document.key). Debounced so rapid dropdown changes collapse into one rewrite. Skipped
  // when the surface can't be previewed (no doc, read-only, or finalized → backend 400s).
  //
  // Proxy DRAFT is deliberately NOT part of this: it only re-attributes the draft to the
  // chosen radiologist, so no signature is stamped for it.
  //
  // The debounced runner reads the latest inputs from a ref (it's created once), matching
  // the editorStateRef pattern used for the OO event handlers above.
  const sigPreviewStateRef = useRef<{ docId: number | null; proxyUser: string | null; coSigningDoctor: string | null; canPreview: boolean }>({
    docId: null,
    proxyUser: null,
    coSigningDoctor: null,
    canPreview: false,
  });
  useEffect(() => {
    sigPreviewStateRef.current = {
      docId: activeDocId,
      // Signature-bearing proxy only — a proxy draft never contributes a signature.
      proxyUser: proxySignoffUser,
      coSigningDoctor: cosigningDoctor,
      // Re-reporting a signed study is previewable now: the editing surface is a scratch
      // fork (an ordinary draft file), not the immutable snapshot, so stamping it is safe.
      //
      // The bar is the document being EDITED, which on a signed order is no longer
      // currentReport's document — so the finalized test has to be scoped to the case where
      // those are the same doc. It stays as a backstop for the fallback path where a fork
      // failed and the snapshot itself is on screen (the backend 400s on it regardless).
      canPreview:
        !!activeDocId && !docViewOnly && !privilegeReadOnly && !isLeaving() &&
        !(activeDocId === currentReport?.document?.doc_id &&
          currentReport?.document?.doc_status === 'finalized'),
    };
  }, [activeDocId, docViewOnly, privilegeReadOnly, orderReportState, currentReport, proxySignoffUser, cosigningDoctor, isLeaving]);

  // Unlock the page and leave the editor exactly as it is (current key, edits intact). Used
  // on every non-success outcome — the editor must NOT be reopened on those paths.
  const unlockSignaturePreview = useCallback((warning: string) => {
    awaitingSigReadyRef.current = false;
    if (sigTimeoutRef.current) { clearTimeout(sigTimeoutRef.current); sigTimeoutRef.current = null; }
    setSignatureUpdating(false);
    message.warning(warning);
  }, []);

  const runSignaturePreview = useRef(
    debounce(() => {
      const { docId, proxyUser, coSigningDoctor, canPreview } = sigPreviewStateRef.current;
      if (!docId || !canPreview) return;
      // Lock ON immediately, before the request is awaited — no typing, Save Draft, Sign Off,
      // or dropdown change is possible from here until the reopened editor is ready.
      setSignatureUpdating(true);
      awaitingSigReadyRef.current = false;
      // 30s backstop: if the request hangs or the reopened editor never reaches
      // onDocumentReady, unlock and leave the editor on the current key.
      if (sigTimeoutRef.current) clearTimeout(sigTimeoutRef.current);
      sigTimeoutRef.current = setTimeout(() => {
        sigTimeoutRef.current = null;
        unlockSignaturePreview('Couldn’t load the signature preview. Please try again.');
      }, 30_000);

      previewSignature(docId, { proxy_user: proxyUser, co_signing_doctor: coSigningDoctor })
        .then(async (res: any) => {
          // Any non-success (e.g. HTTP 200 with success:false) is the error path: leave the
          // editor untouched.
          if (res?.data?.success === false) {
            unlockSignaturePreview('Couldn’t load the signature preview. Please try again.');
            return;
          }
          // Success: reopen on the new key. The lock is released only when the reopened
          // editor fires onDocumentReady (see the editor-init effect) — NOT here.
          awaitingSigReadyRef.current = true;
          destroyEditor();
          await loadEditorConfig(docId);
        })
        .catch((err: any) => {
          // 503 + PREVIEW_RETRY: the backend couldn't confirm the flush of the latest edits.
          // Retriable — keep the editor on the current key so nothing is lost.
          const retriable = err?.response?.status === 503 && err?.response?.data?.code === 'PREVIEW_RETRY';
          unlockSignaturePreview(
            retriable
              ? 'Could not capture your latest edits. Please try again.'
              : 'Couldn’t load the signature preview. Please try again.',
          );
        });
    }, 800),
  ).current;

  // Cancel any pending preview + its safety timeout on unmount so neither fires against a
  // torn-down editor.
  useEffect(() => () => {
    runSignaturePreview.cancel();
    if (sigTimeoutRef.current) { clearTimeout(sigTimeoutRef.current); sigTimeoutRef.current = null; }
  }, [runSignaturePreview]);

  // While locked, pull keyboard focus out of the OO editor. The editor lives in a cross-origin
  // iframe, so the parent can't intercept its keystrokes — but blurring the iframe and focusing
  // our overlay means keystrokes no longer reach the editor. The overlay itself blocks pointer
  // input (pointerEvents:auto), so a click can't refocus the iframe either.
  useEffect(() => {
    if (!signatureUpdating) return;
    try { (document.activeElement as HTMLElement | null)?.blur?.(); } catch {}
    sigLockOverlayRef.current?.focus?.();
  }, [signatureUpdating]);

  // Fire on an actual dropdown change only — skip the initial mount so simply opening a
  // report doesn't rewrite it. Only the signature-bearing selections are watched; picking a
  // proxy-draft radiologist must leave the document untouched. (Switching from proxy signoff
  // to proxy draft still clears proxySignoffUser, so any stamped signature is stripped.)
  const sigPreviewArmedRef = useRef(false);
  useEffect(() => {
    if (!sigPreviewArmedRef.current) { sigPreviewArmedRef.current = true; return; }
    runSignaturePreview();
  }, [proxySignoffUser, cosigningDoctor, runSignaturePreview]);

  // A different study arriving (queue auto-advance after sign-off, or a manual select) must
  // not inherit the previous study's per-report selections. These values live here in the
  // parent; the checkboxes/dropdowns that set them live in <AdvancedOptions>, which is
  // remounted via `key` on the same order id — clear both in lockstep so nothing carries over.
  // diagnosed/correlated are intentionally NOT cleared here: they hold the new study's saved
  // values, repopulated by the ow_diagnosed/ow_correlated effect above.
  useEffect(() => {
    // The signature-bearing selections drive the live signature preview. Clearing them would
    // otherwise trip that effect into rewriting the freshly loaded document, so re-arm its
    // "skip the next change" guard and drop any pending run — keeping this reset inert.
    if (proxySignoffUser || cosigningDoctor) {
      sigPreviewArmedRef.current = false;
      runSignaturePreview.cancel();
    }
    setProxyDraftUser(null);
    setProxySignoffUser(null);
    setCosigningDoctor(null);
  }, [pacs_order?.pacs_ord_id]);

  // Sliding to another study abandons any amendment in progress on the previous one — drop
  // its scratch surface rather than leaving it for the sweeper. A sign-off does not reach
  // here: it consumes the scratch server-side and clears the ref first (see onSaved).
  useEffect(() => {
    return () => { discardScratchSurface(); };
  }, [pacs_order?.pacs_ord_id, discardScratchSurface]);

  // A closed tab or a crash cannot be caught here (sendBeacon can't carry the auth header,
  // and unmount cleanups don't run on tab close). Two backstops cover it: the next fork for
  // this user+order drops their previous scratch server-side, and the hourly sweep reclaims
  // anything idle past SCRATCH_MAX_AGE_HOURS.

  const handleDeleteReport = useCallback(async (report: Report) => {
    const ooReport = report as OoReport;
    const isLastReport = reports.length <= 1;
    const deleted = await deleteReport(ooReport);
    if (!deleted) return;
    if (ooReport.document?.doc_id === activeDocId) {
      destroyEditor();
      setActiveDocId(null);
    }
    if (isLastReport) {
      if (handlePostSaving) markLeaving();
      destroyEditor();
      handlePostSaving?.('DELETED', patientDetails);
    } else {
      refetchReports();
    }
  }, [deleteReport, refetchReports, activeDocId, destroyEditor, reports.length, handlePostSaving, patientDetails]);

  const handleCosigning = useCallback((val: string, _userData: any) => {
    setCosigningDoctor(val);
  }, []);

  const toggleSection = (key: string) =>
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Render ──
  return (
    <div className={`editor-container${focusMode ? ' focus-mode' : ''}`}>
      {/* ── Pane 1: Studies ── */}
      <aside className={`report-pane pane-studies${studiesCollapsed ? ' collapsed' : ''}`}>
        {studiesCollapsed ? (
          <Tooltip title="Show studies" placement="right">
            <button type="button" className="rail-expand-btn" onClick={() => toggleStudiesRail(false)} aria-label="Expand studies panel">
              <RightOutlined />
            </button>
          </Tooltip>
        ) : (
          <>
            <div className="rail-header">
              <span className="rail-header-title">
                <UnorderedListOutlined /> Studies{orders.length ? ` · ${orders.length}` : ''}
              </span>
              <Tooltip title="Hide panel" placement="right">
                <button type="button" className="rail-icon-btn" onClick={() => toggleStudiesRail(true)} aria-label="Collapse studies panel">
                  <LeftOutlined />
                </button>
              </Tooltip>
            </div>
            <div className="rail-scroll">
              <StudyQueue orders={orders} selectedIndex={selectedIndex} loading={queueLoading} onSelect={onSelectOrder} onUnload={onUnloadOrder} disabled={!!pendingAction || handoffPending} />
            </div>
          </>
        )}
      </aside>

      {/* ── Pane 2: Study details ── */}
      <aside className={`report-pane pane-details${detailsCollapsed ? ' collapsed' : ''}`}>
        {detailsCollapsed ? (
          <Tooltip title="Show study details" placement="right">
            <button type="button" className="rail-expand-btn" onClick={() => setDetailsCollapsed(false)} aria-label="Expand details panel">
              <RightOutlined />
            </button>
          </Tooltip>
        ) : (
          <>
            <div className="rail-header">
              <span className="rail-header-title">
                <UserOutlined /> Study Details
              </span>
              <Tooltip title="Hide panel" placement="right">
                <button type="button" className="rail-icon-btn" onClick={() => setDetailsCollapsed(true)} aria-label="Collapse details panel">
                  <LeftOutlined />
                </button>
              </Tooltip>
            </div>
            <div className="rail-scroll">
              {patient ? (
                <>
                  {/* Patient summary */}
                  <div className="patient-card">
                    <div className="patient-card-name">
                      <UserOutlined className="patient-card-icon" />
                      <span>{patient?.pat_name}{patient?.pat_pin ? `, ${patient.pat_pin}` : ''}</span>
                    </div>
                    <div className="patient-card-meta">
                      {patient?.pat_sex || '-'} · {patient?.pat_dob && isValidDate(patient.pat_dob) ? moment(patient.pat_dob).fromNow(true) : '-'} · {pacs_order?.po_modality || '-'}
                    </div>
                    <div className="patient-card-meta">Ref: {pacs_order?.po_ref_doc || '-'}</div>
                    <div className="patient-card-meta">Acc No: {pacs_order?.po_acc_no || '-'}</div>
                    <div className="patient-card-actions">
                      {hasStudyViewingPermission(getUserDetails()) && (
                        <Button
                          className="rail-action-btn"
                          size="small"
                          onClick={async () => {
                            const w = await launchViewerOnDualMonitor(patientDetails!.ps_study_uid, patientDetails!);
                            if (!w) message.warning('Could not open viewer window');
                          }}
                        >
                          Launch Viewer
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Reports History */}
                  <div className="collapsible-section">
                    <div className="collapsible-header" onClick={() => toggleSection('reportsHistory')}>
                      <div className="collapsible-header-left"><BarChartOutlined className="collapsible-icon" /><span className="collapsible-title">Reports History</span></div>
                      {expandedSections.reportsHistory ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                    </div>
                    {expandedSections.reportsHistory && (
                      <div className="collapsible-content collapsible-content--scroll" style={{ paddingTop: 0, paddingBottom: 0 }}>
                        <ReportHistory
                          reports={reports}
                          activeDocId={activeDocId}
                          activeReportId={currentReport?.pr_id ?? null}
                          orderSignedOff={orderReportState === 'SIGNEDOFF'}
                          latestSignedReportId={latestSignedReport?.pr_id ?? null}
                          pacsOrder={pacs_order}
                          onLoad={handleLoadReport}
                          onDelete={handleDeleteReport}
                        />
                      </div>
                    )}
                  </div>

                  {/* Prior Reports */}
                  {priorReports.length > 0 && (
                    <div className="collapsible-section">
                      <div className="collapsible-header" onClick={() => toggleSection('priorReports')}>
                        <div className="collapsible-header-left"><BarChartOutlined className="collapsible-icon" /><span className="collapsible-title">Prior Reports</span></div>
                        {expandedSections.priorReports ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                      </div>
                      {expandedSections.priorReports && (
                        <div className="collapsible-content collapsible-content--scroll" style={{ paddingTop: 0, paddingBottom: 0 }}>
                          <PriorReports reports={priorReports} isSignedOff={isSignedOff} onLoadAsTemplate={handleLoadPriorReport} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Load Template — server-side apply; on a signed-off study the backend forks
                      an editable working copy (see applyTemplate). */}
                  {pacs_order && (
                    <div className="collapsible-section">
                      <div className="collapsible-header" onClick={() => toggleSection('templateLoad')}>
                        <div className="collapsible-header-left"><FileTextOutlined className="collapsible-icon" /><span className="collapsible-title">Load Template</span></div>
                        {expandedSections.templateLoad ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                      </div>
                      {expandedSections.templateLoad && (
                        <div className="collapsible-content">
                          <TemplatePanel
                            variant="panel"
                            pacsOrdId={pacs_order.pacs_ord_id}
                            modality={pacs_order.po_modality}
                            bodyPart={pacs_order.po_body_part}
                            onInsertTemplate={handleInsertTemplate}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Advanced Options */}
                  {REPORT_CONFIG.SHOW_ADVANCED_OPTIONS && (
                    <div className="collapsible-section">
                      <div className="collapsible-header" onClick={() => toggleSection('advancedOptions')}>
                        <div className="collapsible-header-left"><SettingOutlined className="collapsible-icon" /><span className="collapsible-title">Advanced Options</span></div>
                        {expandedSections.advancedOptions ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                      </div>
                      {expandedSections.advancedOptions && (
                        <AdvancedOptions
                          // Remount per order so the checkboxes / dropdowns / activeAction
                          // don't inherit the previous study's selections on queue advance.
                          key={pacs_order?.pacs_ord_id}
                          locked={signatureUpdating}
                          radUsers={radUsers}
                          allUsers={allUsers}
                          onNotifyPhysician={() => setCriticalFindingVisible(true)}
                          onCosignChange={handleCosigning}
                          isSignedOff={isSignedOff}
                          currentUsername={getUserDetails()?.username}
                          onProxyDraftChange={(val) => { setProxyDraftUser(val); setProxySignoffUser(null); }}
                          onProxySignoffChange={(val) => { setProxySignoffUser(val); setProxyDraftUser(null); }}
                          diagnosed={diagnosed}
                          correlated={correlated}
                          onDiagnosedChange={setDiagnosed}
                          onCorrelatedChange={setCorrelated}
                        />
                      )}
                    </div>
                  )}

                  {/* Report submit actions (moved from the editor footer) */}
                  <div className="report-actions-panel">
                    <Button className="footer-btn" type="default" onClick={cancel}>Cancel</Button>
                    {/* Sits with the commit actions because it answers the question they
                        raise — "what am I about to produce?" — but commits nothing. */}
                    <Button
                      className="footer-btn"
                      type="default"
                      icon={<FilePdfOutlined />}
                      loading={pdfPreviewLoading}
                      disabled={!activeDocId || signatureUpdating || !!pendingAction || handoffPending}
                      onClick={handlePreviewPdf}
                    >
                      Preview
                    </Button>
                    <StatusActions
                      statusList={statusList}
                      statusLoading={statusLoading}
                      orderReportState={orderReportState}
                      pendingAction={pendingAction}
                      proxyMode={proxyMode}
                      locked={signatureUpdating}
                      maxLevel={reportingLevel?.level_ordinal ?? null}
                      enforced={!!reportingLevel?.enforcement_enabled}
                      signatureMissing={signatureMissing}
                      onAction={handleSaveForm}
                    />
                  </div>
                </>
              ) : (
                <div className="rail-empty">Select a study to begin</div>
              )}
            </div>
          </>
        )}
      </aside>

      {/* ── Editor (max space) ── */}
      <div className="editor-section">
        <div className="editor-tabs-bar">
          <button
            type="button"
            className={`editor-tab${activeEditorTab === 'his' ? ' active' : ''}`}
            onClick={() => openNotesTab('his')}
          >
            HIS Notes
          </button>
          <button
            type="button"
            className={`editor-tab${activeEditorTab === 'ris' ? ' active' : ''}`}
            onClick={() => openNotesTab('ris')}
          >
            RIS Notes
          </button>
          <button
            type="button"
            className={`editor-tab${activeEditorTab === 'editor' ? ' active' : ''}`}
            onClick={() => setActiveEditorTab('editor')}
          >
            Editor
          </button>
          {onToggleFocusMode && (
            <Tooltip title={`${focusMode ? 'Exit' : 'Enter'} focus mode (Ctrl+Shift+F)`}>
              <Button
                className="editor-tabs-focus-btn"
                type="text"
                size="small"
                icon={focusMode ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={onToggleFocusMode}
              />
            </Tooltip>
          )}
        </div>

        <div className="editor-tab-panes">
          {/* Notes pane — lazy-mounted on first open, then kept alive; the OnlyOffice editor
              pane below is NEVER unmounted, so tab switches only toggle visibility. */}
          {notesVisited && patientDetails && (
            <div className="notes-pane" style={{ display: activeEditorTab === 'editor' ? 'none' : 'block' }}>
              <InlineNotesPanel details={patientDetails} section={notesSection} />
            </div>
          )}

          {/* Editor pane — always mounted */}
          <div className="editor-pane" style={{ display: activeEditorTab === 'editor' ? 'flex' : 'none' }}>
        <div className="section-header">
          <div className="section-header-title">Medical Report</div>
          {signatureUpdating && (
            <span style={{ marginLeft: 12, fontSize: 12, color: '#1D4ED8', background: '#E5EDFF', borderRadius: 4, padding: '2px 8px' }}>
              <Spin size="small" style={{ marginRight: 6 }} /> Applying signature…
            </span>
          )}
          {privilegeReadOnly ? (
            <span style={{ marginLeft: 12, fontSize: 12, color: '#8A6A2E', background: '#F5EFE2', borderRadius: 4, padding: '2px 8px' }}>
              Read-only — you have {reportingLevel?.level === 'NONE' ? 'no reporting access' : 'View access'} for
              {reportingLevel?.owning_modality ? ` ${reportingLevel.owning_modality}` : ' this modality'} at this site
            </span>
          ) : docViewOnly ? (
            <span style={{ marginLeft: 12, fontSize: 12, color: '#8A6A2E', background: '#F5EFE2', borderRadius: 4, padding: '2px 8px' }}>
              Read-only — historical version
            </span>
          ) : currentReport?.pr_status === 'SIGNEDOFF' ? (
            <span style={{ marginLeft: 12, fontSize: 12, color: '#00795C', background: '#DAF3EE', borderRadius: 4, padding: '2px 8px' }}>
              Signed-off report — edits are kept only if you sign off again
            </span>
          ) : null}
        </div>

        <div className="section-content" style={{ padding: 0, position: 'relative' }}>
          {/* Overlay layer — React freely adds/removes nodes here.
              It is a sibling of the OO container layer, NOT a wrapper around it,
              so React never uses any OO-mutated node as an insertBefore reference. */}
          <div className="absolute inset-0" style={{ zIndex: 10, pointerEvents: 'none' }}>
            {!activeDocId && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#45556C', pointerEvents: 'auto' }}>
                {patientDetails ? 'Loading…' : 'Select a study to begin reporting'}
              </div>
            )}
            {activeDocId && editorLoading && !signatureUpdating && !scriptError && !editorError && (
              <div className="flex items-center justify-center" style={{ height: '100%', background: 'rgba(0,0,0,0.3)', pointerEvents: 'auto' }}>
                <Spin size="large" tip="Loading editor..." />
              </div>
            )}
            {activeDocId && scriptError && (
              <div className="flex items-center justify-center bg-white" style={{ height: '100%', pointerEvents: 'auto' }}>
                <Alert type="error" message="Failed to load editor" description={scriptError} action={<Button onClick={retryScript}>Retry</Button>} />
              </div>
            )}
            {activeDocId && editorError && (
              <div className="flex items-center justify-center bg-white" style={{ height: '100%', pointerEvents: 'auto' }}>
                <Alert type="error" message="Editor Error" description={editorError} action={
                  <Button onClick={() => { setEditorError(null); if (activeDocId) loadEditorConfig(activeDocId, docViewOnly || privilegeReadOnly); }}>Retry</Button>
                } />
              </div>
            )}
          </div>
          {/* OO container layer — this div and its children are NEVER conditional.
              React renders exactly one stable child here at all times, so it never
              calls insertBefore/removeChild on nodes that OnlyOffice has mutated. */}
          <div className="h-full w-full">
            <div id="oo-report-editor-container" ref={containerRef} style={{ height: '100%', width: '100%' }} />
          </div>
          {/* Signature-preview lock: a transparent, focusable blocker over the editor iframe.
              It captures pointer input and (once focused, via the effect above) holds keyboard
              focus so nothing reaches the OO editor while the signed doc is being rebuilt and
              reopened. Sits above the OO container and the zIndex:10 status layer. */}
          {signatureUpdating && (
            <div
              ref={sigLockOverlayRef}
              tabIndex={-1}
              className="flex items-center justify-center"
              onMouseDown={(e) => e.preventDefault()}
              style={{
                position: 'absolute', inset: 0, zIndex: 30, outline: 'none',
                background: 'rgba(255,255,255,0.55)', cursor: 'wait',
              }}
            >
              <Spin size="large" tip="Applying signature…" />
            </div>
          )}
        </div>
          </div>{/* .editor-pane */}
        </div>{/* .editor-tab-panes */}
      </div>

      {/* ── Dialogs ── */}
      <TemplateLoadDialog
        visible={templateLoadDialogVisible}
        isPriorReport={pendingPriorDocId != null}
        onAppend={() => (pendingPriorDocId != null
          ? applyPriorReport(pendingPriorDocId, 'append')
          : applyTemplate(pendingTemplateId, 'append'))}
        onReplace={() => (pendingPriorDocId != null
          ? applyPriorReport(pendingPriorDocId, 'replace')
          : applyTemplate(pendingTemplateId, 'replace'))}
        onCancel={() => {
          setTemplateLoadDialogVisible(false);
          setPendingTemplateId(null);
          setPendingPriorDocId(null);
        }}
      />
      <CriticalFindingDialog
        visible={criticalFindingVisible}
        orderDetails={patientDetails}
        onClose={() => setCriticalFindingVisible(false)}
      />
      {/* Rendered from the sign-off pipeline — this is the PDF, not a rendering of the
          editor's content. Read-only: nothing here commits or changes the report. */}
      <Modal
        title="Report Preview — this is the PDF that will be produced"
        open={pdfPreviewOpen}
        onCancel={() => { setPdfPreviewOpen(false); setPdfPreviewData(null); }}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <Spin spinning={pdfPreviewLoading} tip="Building the report preview…">
          <div style={{ height: '80vh', overflowY: 'auto' }}>
            {pdfPreviewData ? (
              <PdfViewer
                pdfArrayBuffer={pdfPreviewData}
                patientData={{ patient: patientDetails?.patient }}
                mimeType="application/pdf"
                hideCopyButton
                hideWhatsappButton
              />
            ) : null}
          </div>
        </Spin>
      </Modal>
    </div>
  );
};

export default OOReportEditor;
